import { Component, OnInit, Input, inject, viewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Receipt } from 'src/app/api/api-campaigns.service.generated';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonComponent } from "wecheer-common";
import { ReceiptPanelDetailsComponent } from "./receipt-panel-details/receipt-panel-details.component";
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-receipt-panel',
  templateUrl: './receipt-panel.component.html',
  styleUrls: ['./receipt-panel.component.scss'],
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    ReceiptPanelDetailsComponent,
    ButtonComponent
  ]
})
export class ReceiptPanelComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbar = inject(MatSnackBar);

  @Input() receipt!: Receipt;
  docId: string | undefined;
  readonly drawer = viewChild<MatDrawer>('drawer');
  readonly receiptImage = viewChild<ElementRef>('receiptImage');
  readonly ocrPanel = viewChild<ElementRef>('ocrPanel');

  drawerWidth = 650; // Default width
  showImageViewer = false;
  showOcrText = false;
  ocrText = '';
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;
  private readonly minWidth = 600; // Minimum width to avoid text overflow
  private readonly maxWidth = 1200;

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      // Don't close drawer if keepPanelOpen flag is set
      if (params['keepPanelOpen'] !== 'true') {
        this.drawer()?.close();
      }
    });

    // Load saved width from localStorage
    const savedWidth = localStorage.getItem('receiptPanelWidth');
    if (savedWidth) {
      this.drawerWidth = parseInt(savedWidth, 10);
    }
  }

  open(receipt: Receipt) {
    // Store the receipt for reference
    this.receipt = new Receipt(receipt);
    // Store the docId to pass to child component
    this.docId = receipt.docId || receipt.id;
    const drawer = this.drawer();
    if (drawer) {
      drawer.open();
    }
  }

  onOpenFull(fullScreen: boolean) {
    if (fullScreen)
      window.open(`receipts/${this.receipt.id}`);
    else
      this.router.navigate(['/receipts', this.receipt.id]);
  }

  onReceiptUpdated(updatedReceipt: Receipt) {
    this.receipt = updatedReceipt;
  }

  openImageViewer(withOcr: boolean = false, ocrText: string = '') {
    this.showImageViewer = true;
    this.showOcrText = withOcr;
    this.ocrText = ocrText;

    const drawer = this.drawer();
    if (drawer) {
      drawer.disableClose = true;
      if (!drawer.opened) {
        drawer.open();
      }
    }

    // Sync heights after view is initialized
    if (withOcr) {
      setTimeout(() => this.onImageLoad(), 200);
    }
  }

  closeImageViewer() {
    this.showImageViewer = false;
    this.showOcrText = false;
    this.ocrText = '';

    const drawer = this.drawer();
    if (drawer) {
      drawer.disableClose = false;
      if (!drawer.opened) {
        drawer.open();
      }
    }
  }

  onResizeStart(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.drawerWidth;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizing) return;

      const deltaX = this.startX - e.clientX; // Negative because we're on the right side
      let newWidth = this.startWidth + deltaX;

      // Constrain width between min and max
      newWidth = Math.max(this.minWidth, Math.min(this.maxWidth, newWidth));

      this.drawerWidth = newWidth;
    };

    const onMouseUp = () => {
      this.isResizing = false;
      // Save width to localStorage
      localStorage.setItem('receiptPanelWidth', this.drawerWidth.toString());

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  async copyOcrText(): Promise<void> {
    if (!this.ocrText) {
      this.snackbar.open('No OCR text to copy', 'Close', { duration: 3000 });
      return;
    }

    try {
      await navigator.clipboard.writeText(this.ocrText);
      this.snackbar.open('OCR text copied to clipboard', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Failed to copy OCR text', error);
      this.snackbar.open('Unable to copy OCR text', 'Close', { duration: 3000 });
    }
  }

  onImageLoad(): void {
    // Sync OCR panel height with image height
    setTimeout(() => {
      const imageEl = this.receiptImage()?.nativeElement;
      const panelEl = this.ocrPanel()?.nativeElement;

      if (imageEl && panelEl && this.showOcrText) {
        const imageHeight = imageEl.offsetHeight;
        panelEl.style.height = `${imageHeight}px`;
      }
    }, 100);
  }
}
